import { FormikContext } from 'formik';
import React, {
	PropsWithChildren,
	ReactNode,
	createContext,
	useContext,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

type ModalData = { children: ReactNode; formik?: any };

const Context = createContext(
	(undefined as unknown) as {
		updateModal: (id: string, data: ModalData) => void;
		hideModal: (id: string) => void;
	},
);

function ModalContainer(props: PropsWithChildren<{ formik?: any }>) {
	if (props.formik) {
		return <FormikContext.Provider value={props.formik}>{props.children}</FormikContext.Provider>;
	} else {
		return <>{props.children}</>;
	}
}

export default function ModalProvider(props: PropsWithChildren<{}>) {
	const [modals, setModals] = useState(new Map<string, ModalData>());

	const updateModal = useRef(function updateModal(id: string, data: ModalData) {
		setModals((modals) => {
			modals = new Map(modals);
			modals.set(id, data);
			return modals;
		});
	}).current;

	const hideModal = useRef(function hideModal(id: string) {
		setModals((modals) => {
			modals = new Map(modals);
			modals.delete(id);
			return modals;
		});
	}).current;

	const value = useRef({ updateModal, hideModal }).current;

	return (
		<Context.Provider value={value}>
			{props.children}
			{[...modals.entries()].map(([id, { children, formik }]) => (
				<ModalContainer key={id} formik={formik}>
					{children}
				</ModalContainer>
			))}
		</Context.Provider>
	);
}

export function ModalPortal(props: PropsWithChildren<{}>) {
	const context = useContext(Context);
	const id = useRef(Math.random().toString()).current;
	const formik = useContext(FormikContext);

	useLayoutEffect(() => {
		context.updateModal(id, {
			formik,
			children: props.children,
		});
	}, [props.children]);

	useLayoutEffect(() => {
		return () => context.hideModal(id);
	}, []);

	return null;
}
